import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { toast } from "sonner";
import { Gamepad2 } from "lucide-react";

const ROMAN_BG = "https://images.pexels.com/photos/159862/art-school-of-athens-raphael-italian-painter-fresco-159862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const SOLO_DIFFICULTIES = [
  { key: "easy",   label: "سهل",   target: 8000,  color: "rgba(74,222,128,0.85)",  glow: "rgba(74,222,128,0.25)" },
  { key: "medium", label: "متوسط", target: 12000, color: "rgba(251,191,36,0.85)",  glow: "rgba(251,191,36,0.25)" },
  { key: "hard",   label: "صعب",   target: 18000, color: "rgba(239,68,68,0.85)",   glow: "rgba(239,68,68,0.25)" },
];

export default function TeamSetupPage() {
  const navigate = useNavigate();
  const { createSession, loading, currentUser, refreshUser, listGameSaves, gameMode, setSoloSetup } = useGame();
  const [team1, setTeam1] = useState("");
  const [team2, setTeam2] = useState("");
  const [focus1, setFocus1] = useState(false);
  const [focus2, setFocus2] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [activeSaves, setActiveSaves] = useState([]);
  // SOLO-specific state
  const [soloDifficulty, setSoloDifficulty] = useState("medium");

  const isSolo = gameMode === "solo";
  const isLoggedIn  = !!currentUser?.id;

  const credits     = currentUser?.game_credits || 0;
  const hadPlan     = !!(currentUser?.last_plan_id);

  const isStarting = useRef(false);

  // Called after the user confirms they want a NEW game (either no saves, or dismissed modal)
  const proceedWithNewGame = async () => {
    if (isStarting.current) return;
    isStarting.current = true;
    setShowResumeModal(false);
    try {
      if (isSolo) {
        const diffConfig = SOLO_DIFFICULTIES.find(d => d.key === soloDifficulty);
        await createSession(team1.trim(), "", "solo");
        setSoloSetup({ playerName: team1.trim(), difficulty: soloDifficulty, target: diffConfig.target });
      } else {
        await createSession(team1.trim(), team2.trim(), "normal");
      }
      if (isLoggedIn) refreshUser();
      navigate("/categories");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "حدث خطأ، حاول مرة ثانية");
    } finally {
      isStarting.current = false;
    }
  };

  const handleStart = async () => {
    if (isSolo) {
      if (!team1.trim()) { toast.error("أدخل اسمك!"); return; }
    } else {
      if (!team1.trim() || !team2.trim()) { toast.error("أدخل اسم الفريقين!"); return; }
      if (team1.trim() === team2.trim()) { toast.error("اسم الفريقين لازم يكون مختلف!"); return; }
    }
    if (isStarting.current) return;

    // Block users with no remaining entitlement (no paid credits AND free trial used)
    const hasFreeTrial = !currentUser?.free_game_used;
    const hasPaidEntitlement = credits > 0 || (currentUser?.community_game_credits > 0);
    if (isLoggedIn && !hasPaidEntitlement && !hasFreeTrial) {
      setShowUpgradeModal(true);
      return;
    }

    // Warn when on last credit
    if (isLoggedIn && credits === 1) {
      toast.warning("تبقّى لديك مباراة واحدة فقط بعد هذه!");
    }

    // Only NOW check for active saves — triggered by user action, not mount
    if (isLoggedIn && listGameSaves) {
      try {
        const saves = await listGameSaves();
        const active = (saves || []).filter(s => s.status === "paused" || s.status === "active");
        if (active.length > 0) {
          setActiveSaves(active);
          setShowResumeModal(true);
          return; // Wait for user choice in modal
        }
      } catch {}
    }

    await proceedWithNewGame();
  };

  const inputStyle = (focused) => ({
    width: "100%",
    background: focused ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.04)",
    border: `1.5px solid ${focused ? "rgba(241,225,148,0.55)" : "rgba(241,225,148,0.16)"}`,
    borderRadius: "14px",
    padding: "14px 18px",
    fontSize: "1.05rem",
    fontWeight: 700,
    color: "#F1E194",
    outline: "none",
    fontFamily: "Cairo, sans-serif",
    direction: "rtl",
    textAlign: "right",
    transition: "border-color 0.2s, background 0.2s",
    boxSizing: "border-box",
  });

  return (
    <>
    <div style={{
      minHeight: "100svh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px 16px",
      background: "#060001",
      fontFamily: "Cairo, sans-serif",
      direction: "rtl",
      position: "relative",
      overflow: "hidden",
    }}>
      <style>{`
        .team-input::placeholder { color: rgba(241,225,148,0.25); font-weight: 500; }
        @keyframes cardIn { from { opacity:0; transform:translateY(24px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
      `}</style>

      {/* Art bg */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0,
        backgroundImage: `url("${ROMAN_BG}")`,
        backgroundSize: "cover", backgroundPosition: "center 30%",
        filter: "brightness(0.26) saturate(0.55) sepia(0.2)",
      }} />
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 1,
        background: "radial-gradient(ellipse 120% 80% at 50% 0%, rgba(61,8,16,0.85) 0%, rgba(6,0,1,0.72) 100%)",
      }} />
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 2,
        background: "radial-gradient(ellipse 100% 100% at 50% 50%, transparent 25%, rgba(2,0,0,0.5) 75%, rgba(0,0,0,0.85) 100%)",
      }} />

      <div style={{ position: "relative", zIndex: 10, width: "100%", maxWidth: "520px", animation: "cardIn 0.45s cubic-bezier(0.34,1.10,0.64,1) both" }}>

        {/* Back */}
        <button
          data-testid="back-btn"
          onClick={() => navigate("/mode")}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            color: "rgba(241,225,148,0.38)", background: "none", border: "none",
            fontFamily: "Cairo, sans-serif", fontWeight: 700, fontSize: "0.88rem",
            cursor: "pointer", marginBottom: "20px", padding: 0,
            transition: "color 0.18s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "rgba(241,225,148,0.72)"}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(241,225,148,0.38)"}
        >
          ← رجوع
        </button>

        {/* Card */}
        <div style={{
          background: "rgba(10,1,2,0.78)",
          border: "1.5px solid rgba(241,225,148,0.13)",
          borderRadius: "28px",
          padding: "clamp(32px,5vw,52px) clamp(24px,4vw,48px)",
          backdropFilter: "blur(24px)",
          boxShadow: "0 0 100px rgba(0,0,0,0.65), 0 0 40px rgba(91,14,20,0.28)",
        }}>

          {/* Header */}
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: "72px", height: "72px", borderRadius: "50%",
              background: isSolo
                ? "radial-gradient(circle, rgba(30,80,200,0.5) 0%, rgba(4,10,40,0.8) 100%)"
                : "radial-gradient(circle, rgba(91,14,20,0.6) 0%, rgba(30,4,8,0.8) 100%)",
              border: "2px solid rgba(241,225,148,0.22)",
              boxShadow: isSolo
                ? "0 0 32px rgba(147,197,253,0.15), 0 0 64px rgba(30,80,200,0.3)"
                : "0 0 32px rgba(241,225,148,0.12), 0 0 64px rgba(91,14,20,0.4)",
              fontSize: "2rem",
              marginBottom: "18px",
            }}>{isSolo ? "🎯" : "⚔️"}</div>

            <h1 style={{
              fontSize: "clamp(1.5rem,4vw,2rem)",
              fontWeight: 900,
              color: "#F1E194",
              margin: "0 0 6px",
              fontFamily: "Cairo, sans-serif",
              textShadow: "0 0 32px rgba(241,225,148,0.3)",
            }}>{isSolo ? "اسمك وصعوبتك" : "سمّوا فرقكم"}</h1>
            <p style={{ color: "rgba(241,225,148,0.38)", fontSize: "0.9rem", margin: 0 }}>
              {isSolo ? "أدخل اسمك واختر مستوى التحدي" : "اختاروا اسماً حماسياً لكل فريق"}
            </p>
          </div>

          {/* Tip */}
          {!isSolo && (
            <div
              data-testid="team-size-recommendation"
              style={{
                background: "rgba(241,225,148,0.05)",
                border: "1px solid rgba(241,225,148,0.14)",
                borderRadius: "12px",
                padding: "10px 16px",
                textAlign: "center",
                color: "rgba(241,225,148,0.40)",
                fontSize: "0.82rem",
                marginBottom: "28px",
              }}
            >
              💡 يُنصح بلاعبين أو أكثر لكل فريق للاستمتاع الكامل
            </div>
          )}

          {/* SOLO explanation */}
          {isSolo && (
            <div style={{
              textAlign: "center",
              color: "rgba(241,225,148,0.50)",
              fontSize: "0.82rem",
              marginBottom: "22px",
              lineHeight: 1.65,
            }}>
              <div>تلعب لحالك على نفس لوحة حُجّة: 6 فئات و36 سؤالًا.</div>
              <div>اختر مستوى التحدي واجمع النقاط المطلوبة قبل نهاية اللوحة 🎯</div>
            </div>
          )}

          {/* Player name (SOLO) or Team 1 (standard) */}
          <div style={{ marginBottom: isSolo ? "24px" : "8px" }}>
            <label
              data-testid="team1-label"
              style={{ display: "block", fontWeight: 700, color: isSolo ? "rgba(147,197,253,0.85)" : "rgba(239,68,68,0.85)", fontSize: "0.88rem", marginBottom: "8px" }}
            >
              {isSolo ? "🎮 اسمك" : "🔴 الفريق الأول"}
            </label>
            <input
              data-testid="team1-input"
              className="team-input"
              type="text"
              value={team1}
              onChange={(e) => setTeam1(e.target.value)}
              placeholder={isSolo ? "مثال: عامر" : "مثال: الأسود"}
              maxLength={20}
              style={inputStyle(focus1)}
              onFocus={() => setFocus1(true)}
              onBlur={() => setFocus1(false)}
              onKeyDown={(e) => {
                if (isSolo && e.key === "Enter") handleStart();
                else if (!isSolo && e.key === "Enter") document.getElementById("team2-input")?.focus();
              }}
            />
          </div>

          {/* Difficulty picker (SOLO only) */}
          {isSolo && (
            <div style={{ marginBottom: "28px" }}>
              <label style={{ display: "block", fontWeight: 700, color: "rgba(241,225,148,0.65)", fontSize: "0.88rem", marginBottom: "12px" }}>
                🎯 مستوى التحدي
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
                {SOLO_DIFFICULTIES.map(d => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setSoloDifficulty(d.key)}
                    style={{
                      padding: "12px 8px",
                      borderRadius: "14px",
                      border: `1.5px solid ${soloDifficulty === d.key ? d.color : "rgba(241,225,148,0.14)"}`,
                      background: soloDifficulty === d.key ? `rgba(${d.color.includes("74,222") ? "74,222,128" : d.color.includes("251,191") ? "251,191,36" : "239,68,68"},0.12)` : "rgba(255,255,255,0.03)",
                      color: soloDifficulty === d.key ? d.color : "rgba(241,225,148,0.45)",
                      fontFamily: "Cairo, sans-serif",
                      fontWeight: 800,
                      fontSize: "0.9rem",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      boxShadow: soloDifficulty === d.key ? `0 0 16px ${d.glow}` : "none",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: "1.2rem", marginBottom: "4px" }}>
                      {d.key === "easy" ? "😊" : d.key === "medium" ? "😤" : "🔥"}
                    </div>
                    <div>{d.label}</div>
                    <div style={{ fontSize: "0.72rem", opacity: 0.7, marginTop: "2px" }}>
                      {d.target.toLocaleString()} نقطة
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* VS Divider + Team 2 (standard only) */}
          {!isSolo && (
            <>
              {/* VS Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: "14px", margin: "20px 0" }}>
                <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, transparent, rgba(241,225,148,0.25))" }} />
                <span style={{ color: "rgba(241,225,148,0.50)", fontWeight: 900, fontSize: "0.95rem", letterSpacing: "0.1em" }}>VS</span>
                <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, rgba(241,225,148,0.25), transparent)" }} />
              </div>

              {/* Team 2 */}
              <div style={{ marginBottom: "32px" }}>
                <label
                  data-testid="team2-label"
                  style={{ display: "block", fontWeight: 700, color: "rgba(59,130,246,0.85)", fontSize: "0.88rem", marginBottom: "8px" }}
                >
                  🔵 الفريق الثاني
                </label>
                <input
                  id="team2-input"
                  data-testid="team2-input"
                  className="team-input"
                  type="text"
                  value={team2}
                  onChange={(e) => setTeam2(e.target.value)}
                  placeholder="مثال: النسور"
                  maxLength={20}
                  style={inputStyle(focus2)}
                  onFocus={() => setFocus2(true)}
                  onBlur={() => setFocus2(false)}
                  onKeyDown={(e) => e.key === "Enter" && handleStart()}
                />
              </div>
            </>
          )}

          {/* CTA */}
          <button
            data-testid="start-game-btn"
            onClick={handleStart}
            disabled={loading}
            style={{
              width: "100%",
              padding: "15px",
              borderRadius: "999px",
              border: "none",
              background: loading ? "rgba(241,225,148,0.30)" : "linear-gradient(135deg, #F5E99A, #D4BC30)",
              color: "#130104",
              fontWeight: 900,
              fontSize: "1.05rem",
              fontFamily: "Cairo, sans-serif",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 0 32px rgba(241,225,148,0.28), 0 4px 20px rgba(0,0,0,0.4)",
              transition: "all 0.2s",
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ""; }}
          >
            {loading ? "جاري التحضير..." : "التالي ←"}
          </button>
        </div>
      </div>
    </div>

    {/* ── Resume modal (shown when user has active/paused saves) ── */}
    {showResumeModal && (
      <div
        onClick={() => setShowResumeModal(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "rgba(12,2,4,0.98)",
            border: "1px solid rgba(241,225,148,0.18)",
            borderRadius: 24,
            padding: "32px 28px",
            maxWidth: 380,
            width: "100%",
            textAlign: "center",
            fontFamily: "Cairo, sans-serif",
            direction: "rtl",
            boxShadow: "0 0 80px rgba(0,0,0,0.9), 0 0 40px rgba(91,14,20,0.3)",
          }}
        >
          {/* Dual icons: warning + gamepad */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"10px", marginBottom:16 }}>
            <span style={{ fontSize:"2rem", lineHeight:1 }}>⚠️</span>
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"center",
              width:"40px", height:"40px", borderRadius:"50%",
              background:"rgba(91,14,20,0.32)",
              border:"1.5px solid rgba(241,225,148,0.22)",
              boxShadow:"0 0 16px rgba(241,225,148,0.12)",
            }}>
              <Gamepad2 size={20} color="#F1E194" />
            </div>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: "#F1E194", marginBottom: 8 }}>
            عندك مباراة ما خلصتها!
          </h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: 8 }}>
            {activeSaves.length > 1
              ? `لديك ${activeSaves.length} مباريات محفوظة. هل تريد الاستكمال أو بدء جديدة؟`
              : activeSaves[0]?.game_mode === "solo"
                ? `«${activeSaves[0]?.solo_player_name || activeSaves[0]?.team1_name || "لاعب"} — SOLO ${activeSaves[0]?.solo_difficulty || ""}»`
                : `«${activeSaves[0]?.team1_name || "فريق"} vs ${activeSaves[0]?.team2_name || "فريق"}»`
            }
          </p>
          {activeSaves.length === 1 && (
            <p style={{ fontSize: 12, color: "rgba(241,225,148,0.35)", marginBottom: 20 }}>
              المباراة المحفوظة — هل تريد الاستكمال أو بدء جديدة؟
            </p>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
            <button
              onClick={() => navigate("/my-games")}
              style={{
                background: "linear-gradient(135deg, #F5E99A, #D4BC30)",
                color: "#130104",
                border: "none",
                borderRadius: 14,
                padding: "14px 20px",
                fontWeight: 900,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              استكمال المباراة ←
            </button>
            <button
              onClick={proceedWithNewGame}
              style={{
                background: "rgba(91,14,20,0.25)",
                color: "rgba(241,225,148,0.7)",
                border: "1px solid rgba(91,14,20,0.45)",
                borderRadius: 14,
                padding: "12px 20px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              بدء مباراة جديدة
            </button>
            <button
              onClick={() => setShowResumeModal(false)}
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.28)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 14,
                padding: "10px 20px",
                fontWeight: 700,
                fontSize: 12,
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              إلغاء
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Upgrade modal (shown when credits exhausted) ── */}
    {showUpgradeModal && (
      <div
        onClick={() => setShowUpgradeModal(false)}
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(0,0,0,0.82)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: "rgba(12,2,4,0.98)",
            border: "1px solid rgba(241,225,148,0.18)",
            borderRadius: 24,
            padding: "32px 28px",
            maxWidth: 360,
            width: "100%",
            textAlign: "center",
            fontFamily: "Cairo, sans-serif",
            direction: "rtl",
            boxShadow: "0 0 80px rgba(0,0,0,0.9), 0 0 40px rgba(91,14,20,0.3)",
          }}
        >
          <div style={{ fontSize: 44, marginBottom: 16 }}>🎮</div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: "#f8f2e7", marginBottom: 8 }}>
            انتهت ألعابك
          </h2>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.7, marginBottom: 24 }}>
            استهلكت جميع ألعابك في الباقة الحالية.
            جدّد الآن للاستمرار في اللعب.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={() => navigate("/pricing")}
              style={{
                background: "linear-gradient(135deg, #f2b85b, #e09a2a)",
                color: "#1a0f10",
                border: "none",
                borderRadius: 14,
                padding: "14px 20px",
                fontWeight: 900,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              اختر باقة للاستمرار ✦
            </button>
            <button
              onClick={() => setShowUpgradeModal(false)}
              style={{
                background: "transparent",
                color: "rgba(255,255,255,0.3)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 14,
                padding: "12px 20px",
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "Cairo, sans-serif",
              }}
            >
              إلغاء
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
